import { useAuth } from "../../store";
import apiEndpoints from "../endpoints";
import apiClient from "../exportClient";

/**
 * Leave Request Payload Interface
 * Defines the structure for creating new leave requests
 */
interface LeaveRequestPayload {
  student: number;
  teacher: number;
  start_date: string;
  end_date: string;
  grade: number;
  reason: string;
  other?: string;
  recovery_method: string;
  student_name: string;
  is_start_date: string;
  is_end_date: string;
  terms_accepted: boolean;
}

/**
 * Leave API Methods
 * Provides role-based leave request management and approval workflows
 */
const leaveApis = (id?: any) => {
  const user_id = useAuth.getState().user.user_id;
  const role = useAuth.getState().user.role;
  const userInfo = useAuth.getState().user.info;
  // Get lea_id and school_id from user info for multi-capability users
  // These are returned from /api/user/profile/ endpoint and stored in user.info
  const lea_id = userInfo?.lea_id;
  const school_id = userInfo?.school_id;
  //  { console.log(role)}

  return {
    // Get leave requests for parent users
    getParentLeaveRequests: {
      queryKey: ["Get-list-leave-requests", user_id],
      queryFn: () => {
        return apiClient.get(apiEndpoints.leaves.parentRequestList + user_id);
      },
    },

    // Get status information for a specific leave request
    getStatues: {
      queryKey: ["Get-leave-status", id],
      queryFn: () => {
        if (id) {
          return apiClient.get(apiEndpoints.leaves.getLeaveStatus + id + "/");
        } else {
          return Promise.resolve();
        }
      },
    },

    // Get detailed information for a specific leave request
    getLeaveDetails: {
      queryKey: ["Get-leave-details", id],
      queryFn: () => {
        if (id) {
          return apiClient.get(
            apiEndpoints.leaves.getLeaveRequestDetails + id + "/",
          );
        } else {
          return Promise.reject(new Error("Leave ID is required"));
        }
      },
    },

    /**
     * Get filtered leave requests based on user role and context
     * Handles complex filtering logic for different user types and statuses
     */
    getFilteredLeaves: {
      queryKey: ["getFilteredLeaves", user_id],
      queryFn: (e?: any) => {
        const view = typeof e === "string" ? e : e?.view;
        const teacherId = typeof e === "object" ? e?.teacherId : undefined;
        const page = typeof e === "object" ? e?.page : undefined;
        // Allow overriding the effective role for multi-capability users
        const effectiveRole =
          typeof e === "object" && e?.effectiveRole ? e.effectiveRole : role;
        // Flag to indicate if this is a multi-capability user who wants to see all teachers
        // When true and no specific teacherId is selected, don't filter by teacher
        const isMultiCapabilityUser =
          typeof e === "object" && e?.isMultiCapabilityUser === true;

        // Determine the correct ID to use based on role
        // For LEA_Admin: use lea_id from profile if available (multi-capability users)
        // For School_Admin: use school_id from profile if available (multi-capability users)
        // Otherwise use user_id (primary role user)
        const getEffectiveUserId = (forRole: string) => {
          if (forRole === "LEA_Admin" && lea_id) {
            return lea_id;
          }
          if (forRole === "School_Admin" && school_id) {
            return school_id;
          }
          return user_id;
        };

        let baseParam: string | undefined;
        let baseValue: number | undefined;

        if (effectiveRole === "School_Admin") {
          baseParam = "school";
          baseValue = Number(getEffectiveUserId("School_Admin"));
        }

        if (effectiveRole === "AttandanceCoordinator") {
          baseParam = "attendace_coordinator";
          baseValue = Number(user_id);
        }

        if (effectiveRole === "Teacher") {
          baseParam = "teacher";
          baseValue = Number(user_id);
        }

        if (effectiveRole === "LEA_Admin") {
          baseParam = "district_admin";
          baseValue = Number(getEffectiveUserId("LEA_Admin"));
        }

        // Map role to API parameter name
        const userType =
          effectiveRole === "Teacher"
            ? "teacher"
            : effectiveRole === "Parent"
              ? "parent"
              : effectiveRole === "Student"
                ? "student"
                : effectiveRole === "AttandanceCoordinator"
                  ? "attendace_coordinator"
                  : effectiveRole === "LEA_Admin"
                    ? "district_admin"
                    : effectiveRole === "School_Admin"
                      ? "school"
                      : "clerk";

        // Get the correct user ID value for the API call
        const userIdValue =
          effectiveRole === "LEA_Admin"
            ? getEffectiveUserId("LEA_Admin")
            : effectiveRole === "School_Admin"
              ? getEffectiveUserId("School_Admin")
              : user_id;

        // Fetch all requests for this user
        if (view === "all") {
          return apiClient.get(apiEndpoints.leaves.getFilteredLeaves, {
            params: {
              ...(baseParam && baseValue !== undefined
                ? { [baseParam]: baseValue }
                : {}),
              ...(teacherId !== undefined && teacherId !== null
                ? { teacher: teacherId }
                : {}),
              ...(page !== undefined ? { page } : {}),
            },
          });
        }

        // School Admin: Pending principal approvals (only where tasks are assigned)
        if (
          effectiveRole === "School_Admin" &&
          (view === "requests" || e === "requests")
        ) {
          return apiClient.get(apiEndpoints.leaves.getFilteredLeaves, {
            params: {
              principal_approved: "pending",
              school: userIdValue,
              tasks_assigned: "True", // Only show requests where teacher has assigned work
              ...(teacherId !== undefined && teacherId !== null
                ? { teacher: teacherId }
                : {}),
              ...(page !== undefined ? { page } : {}),
            },
          });
        }

        // LEA Admin: Requests requiring LEA approval (10+ days)
        if (
          effectiveRole === "LEA_Admin" &&
          (view === "approval" || e === "approval")
        ) {
          return apiClient.get(apiEndpoints.leaves.getFilteredLeaves, {
            params: {
              status1: "LEA_Approval",
              district_admin: userIdValue,
              lea_approved: "pending",
              ...(teacherId !== undefined && teacherId !== null
                ? { teacher: teacherId }
                : {}),
              ...(page !== undefined ? { page } : {}),
            },
          });
        }

        // Parent: Requests pending parent approval
        if (
          effectiveRole === "Parent" &&
          (view === "requests" || e === "requests")
        ) {
          return apiClient.get(apiEndpoints.leaves.getFilteredLeaves, {
            params: {
              parent_approved: "pending",
              parent: user_id,
              ...(page !== undefined ? { page } : {}),
            },
          });
        }

        // Teacher: Requests requiring modification (MF = Modification Required)
        if (effectiveRole === "Teacher" && (view === "MF" || e === "MF")) {
          // For multi-capability users with no specific teacher selected, don't filter by teacher
          // For single-role teachers, always use their own user_id
          const teacherParam = teacherId
            ? { teacher: teacherId, principal_suggestion: true }
            : isMultiCapabilityUser
              ? { principal_suggestion: true } // Show all teachers with MF
              : { teacher: user_id, principal_suggestion: true }; // Single teacher

          return apiClient.get(apiEndpoints.leaves.getFilteredLeaves, {
            params: {
              ...teacherParam,
              ...(page !== undefined ? { page } : {}),
            },
          });
        }

        // Teacher: All assigned requests
        if (
          effectiveRole === "Teacher" &&
          (view === "assigned" || e === "assigned")
        ) {
          // For multi-capability users: show all assigned (no teacher filter)
          // For single-role teachers: filter by their own ID
          const teacherParam = isMultiCapabilityUser
            ? { tasks_assigned: "True" } // Multi-capability - show all assigned
            : { tasks_assigned: "True", teacher: user_id }; // Single teacher - filter by own ID

          return apiClient.get(apiEndpoints.leaves.getFilteredLeaves, {
            params: {
              ...teacherParam,
              ...(page !== undefined ? { page } : {}),
            },
          });
        }

        // Teacher: Pending requests awaiting teacher action
        if (
          effectiveRole === "Teacher" &&
          (view === "pending" ||
            view === "requests" ||
            e === "pending" ||
            e === "requests")
        ) {
          // task_teacher_pending always uses the current user's ID
          // For multi-capability users with "All Teachers": only task_teacher_pending with user's ID
          // For single-role teachers: both task_teacher_pending and teacher filters with user's ID
          let teacherParam;

          if (isMultiCapabilityUser) {
            // Multi-capability user - only task_teacher_pending with their own ID
            teacherParam = { task_teacher_pending: user_id };
          } else {
            // Single-role teacher - use both filters with their own ID
            teacherParam = { teacher: user_id, task_teacher_pending: user_id };
          }

          return apiClient.get(apiEndpoints.leaves.getFilteredLeaves, {
            params: {
              ...teacherParam,
              ...(page !== undefined ? { page } : {}),
            },
          });
        }

        // console.log("role", role, e);

        // Default: Determine status based on role and context
        const userBasedStatus =
          effectiveRole === "Teacher"
            ? "IS_Requested"
            : effectiveRole === "Parent"
              ? "ParentReview"
              : effectiveRole === "Student"
                ? "StudentReview"
                : effectiveRole === "AttandanceCoordinator"
                  ? "AttendanceCoordinatorReview"
                  : effectiveRole === "LEA_Admin" &&
                      (view === "requests" || e === "requests")
                    ? "LEAReview"
                    : effectiveRole === "LEA_Admin" &&
                        (view === "approval" || e === "approval")
                      ? "LEA_Approval"
                      : effectiveRole === "School_Admin" &&
                          (view === "requests" || e === "requests")
                        ? "PrincipalReview"
                        : effectiveRole === "School_Admin" &&
                            (view === "approval" || e === "approval")
                          ? "Principal_Approval"
                          : "clerk";

        return apiClient.get(apiEndpoints.leaves.getFilteredLeaves, {
          params: {
            status1: userBasedStatus,
            [userType]: userIdValue,
            ...(teacherId !== undefined && teacherId !== null
              ? { teacher: teacherId }
              : {}),
            ...(page !== undefined ? { page } : {}),
          },
        });
      },
    },

    // Get leave requests for teacher or clerk users
    getTeacherLeaveRequests: {
      queryKey: ["teacher-list-leave-requests", user_id, role],
      queryFn: (teacher: any) => {
        return apiClient.get(
          role === "Teacher"
            ? apiEndpoints.leaves.teacherRequestList + user_id
            : teacher
              ? apiEndpoints.leaves.getLeaveRequestsForClerk +
                `?teacher=${teacher}`
              : apiEndpoints.leaves.getLeaveRequestsForClerk,
        );
      },
    },

    // Get detailed status for a specific leave request
    getLeaveStatus: {
      staleTime: 0,
      refetchOnWindowFocus: true,
      queryKey: ["Get-leave-status", id],
      queryFn: () => {
        return apiClient.get(apiEndpoints.leaves.getStatus + id + "/");
      },
    },

    // Create a new leave request (student/parent initiated)
    raiseLeaveRequest: {
      mk: ["Create-leave-request"],
      mf: async (values: LeaveRequestPayload) => {
        return apiClient.post(
          apiEndpoints.leaves.raiseRequest + user_id + "/",
          values,
        );
      },
    },

    // Create a manual leave request (admin/coordinator initiated)
    manualLeaveRequest: {
      mk: ["raise-manual-leave-request"],
      mf: async (values: any) => {
        return apiClient.post(
          apiEndpoints.leaves.raiseLeaveRequestManual + user_id + "/",
          values,
        );
      },
    },
    getLeaveRequestfilter: (student_id: number) => ({
      queryKey: ["get-LeaveRequest-filter", student_id],
      queryFn: () =>
        apiClient.get(apiEndpoints.leaves.getleaverequestfilter + student_id),
    }),

    getStudentList: (user_id: number, search: any) => ({
      queryKey: ["get-Student-List", user_id],
      queryFn: () =>
        apiClient.get(`${apiEndpoints.leaves.getstudentslist}${user_id}/`, {
          params: { q: search },
        }),
    }),

    /**
     * Update leave request status based on user role
     * Handles approval workflow with role-specific logic and parameters
     * @param effectiveRole - Override role for multi-capability users (e.g., School Admin acting as ATC)
     */
    updateLeaveStatus: {
      mk: ["update-leave-status", user_id, id],
      mf: ({
        signature,
        tasks,
        taskStatus,
        specialEd = false,
        actionType,
        modificationText,
        effectiveRole,
      }: {
        signature?: string | undefined;
        tasks?: any[];
        taskStatus?: any;
        specialEd?: boolean;
        actionType?: "MR" | "accept" | "reject" | string;
        modificationText?: string;
        submissionDate?: string;
        effectiveRole?: string; // Override role for multi-capability users
      }) => {
        // Use effective role if provided, otherwise fall back to primary role
        const activeRole = effectiveRole || role;

        // Teacher: Currently disabled (returns empty promise)
        if (activeRole === "Teacher") {
          return Promise.resolve();
          // return apiClient.post(
          //   apiEndpoints.leaves.updateLeaveStatus + id + '/',
          //   {
          //     status: 'StudentReview',
          //     submission_date: submissionDate,
          //   }
          // );
        }
        // School Admin: Handle principal approval workflow
        else if (activeRole === "School_Admin") {
          // Move to LEA approval for 10+ day requests
          if (taskStatus === "Principal_Approval") {
            return apiClient.post(
              apiEndpoints.leaves.updateLeaveStatus + id + "/",
              {
                status: "LEA_Approval",
                principal_leave_signature: signature,
              },
            );
          } else {
            // Request modification from teacher
            if (actionType === "MR") {
              return apiClient.post(
                apiEndpoints.leaves.updatePrincipalSuggestion + id + "/",
                {
                  suggestions: [
                    {
                      principal: modificationText,
                    },
                  ],
                },
              );
            }
            // Approve request and add signature
            else {
              return apiClient.post(
                apiEndpoints.leaves.principalApproves + id + "/",
                {
                  principal_signature: signature,
                  principal_approved: "accepted",
                  specialeducationservices: specialEd,
                },
              );
            }
          }
        }
        // Parent: Approve with parent signature
        else if (activeRole === "Parent") {
          return apiClient.post(apiEndpoints.leaves.parentApproves + id + "/", {
            parent_signature: signature,
            parent_approved: "accepted",
          });
        }
        // Attendance Coordinator: Approve with tasks assignment
        else if (activeRole === "AttandanceCoordinator") {
          return apiClient.post(apiEndpoints.leaves.acApprove, {
            status: "LEAReview",
            coordinator_signature: signature,
            tasks: tasks,
          });
        }
        // LEA Admin: Handle district-level approvals
        else if (activeRole === "LEA_Admin") {
          // Final approval for 10+ day requests
          if (taskStatus === "LEA_Approval") {
            return apiClient.post(
              apiEndpoints.leaves.updateLeaveStatus + id + "/",
              {
                status: "IS_Requested",
                lea_leave_signature: signature,
              },
            );
          }
          // Standard LEA approval with task completion
          else {
            return apiClient.post(apiEndpoints.leaves.leaAdminApproves, {
              status: "Completed",
              lea_signature: signature,
              tasks: tasks,
            });
          }
        } else {
          return null;
        }
      },
    },

    // Get leaves approved by ADA (Average Daily Attendance)
    getAdaApprovedLeaves: {
      queryKey: ["getAdaApprovedLeaves"],
      queryFn: () => {
        return apiClient.get(apiEndpoints.leaves.getAdaApprovedLeaves);
      },
    },

    // Download ADA report for a specific leave request
    downloadADAreport: {
      queryKey: ["downloadADAreport"],
      queryFn: (leaveId: any) => {
        return apiClient.get(
          apiEndpoints.leaves.downlaodADAReport + leaveId + "/",
        );
      },
    },

    // Skip/Update leave status (for school admin to manually progress status)
    skipLeaveStatus: {
      mk: ["skip-leave-status", id],
      mf: async (newStatus: string) => {
        return apiClient.post(apiEndpoints.leaves.skipLeaveStatus + id + "/", {
          status: newStatus,
        });
      },
    },

    // Notify leave request (when status is IS_Requested for <10 days)
    notifyLeaveRequest: {
      mk: ["notify-leave-request", id],
      mf: async () => {
        return apiClient.post(
          apiEndpoints.leaves.notifyLeaveRequest + id + "/",
        );
      },
    },

    // Notify teacher (when status is StudentReview for >=10 days - waiting for assignment)
    notifyTeacher: {
      mk: ["notify-teacher", id],
      mf: async () => {
        return apiClient.post(apiEndpoints.leaves.notifyTeacher + id + "/");
      },
    },

    // Notify ATC (when status is AttendanceCoordinatorReview - waiting for ATC approval)
    notifyAtc: {
      mk: ["notify-atc", id],
      mf: async () => {
        return apiClient.post(apiEndpoints.leaves.notifyAtc + id + "/");
      },
    },

    // Notify LEA Admin (when status is LEAReview - waiting for LEA approval)
    notifyLea: {
      mk: ["notify-lea", id],
      mf: async () => {
        return apiClient.post(apiEndpoints.leaves.notifyLea + id + "/");
      },
    },
  };
};

export default leaveApis;
